import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { portalApi, portalError, frDate } from "@/lib/weddingApi";
import { Check, CloudOff, Loader2, Users, UtensilsCrossed, ClipboardList, CalendarCheck } from "lucide-react";
import ReceptionTab from "./ReceptionTab";
import GuestsTab from "./GuestsTab";
import MenusTab from "./MenusTab";
import SummaryTab from "./SummaryTab";

const TABS = [
  { key: "reception", label: "Notre réception", short: "Réception", icon: CalendarCheck },
  { key: "guests", label: "Nos invités", short: "Invités", icon: Users },
  { key: "menus", label: "Nos menus", short: "Menus", icon: UtensilsCrossed },
  { key: "summary", label: "Récapitulatif", short: "Récap", icon: ClipboardList },
];

function SaveBadge({ state }) {
  if (state === "saving")
    return <span data-testid="save-state" className="flex items-center gap-1.5 text-xs text-[#697585]"><Loader2 size={12} className="animate-spin" /> Enregistrement…</span>;
  if (state === "saved")
    return <span data-testid="save-state" className="flex items-center gap-1.5 text-xs text-emerald-700"><Check size={12} /> Enregistré</span>;
  if (state === "error")
    return <span data-testid="save-state" className="flex items-center gap-1.5 text-xs text-red-600"><CloudOff size={12} /> Échec — vos saisies sont conservées</span>;
  return <span data-testid="save-state" className="text-xs text-transparent">—</span>;
}

export default function ClientWedding() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("reception");
  const [saveState, setSaveState] = useState("idle");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await portalApi.get("/me");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) nav("/espace-mariage/acces", { replace: true });
      else setError(portalError(e));
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (payload) => {
    if (!data?.wedding) return false;
    setSaveState("saving");
    setError("");
    try {
      const res = await portalApi.patch("/me", { expected_version: data.wedding.version, ...payload });
      setData(res.data);
      setSaveState("saved");
      return true;
    } catch (e) {
      setSaveState("error");
      setError(portalError(e));
      return false;
    }
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {error ? (
          <p data-testid="portal-error" className="text-sm text-red-600 px-6">{error}</p>
        ) : (
          <p className="flex items-center gap-2 text-sm text-[#697585]"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        )}
      </div>
    );
  }

  const w = data.wedding;
  const presentation = data.presentation || {};
  const Tab = { reception: ReceptionTab, guests: GuestsTab, menus: MenusTab, summary: SummaryTab }[tab];
  const services = w.service_config || { adult_services: ["starter", "main", "dessert"], child_services: ["main", "dessert"] };
  const progress = (() => {
    const present = (w.guests || []).filter((g) => g.attending);
    if (!present.length) return 0;
    const complete = present.filter((g) => {
      const needed = g.kind === "child" ? services.child_services : services.adult_services;
      return (needed || []).every((s) => g[{ starter: "starter_id", main: "main_id", dessert: "dessert_id" }[s]]);
    }).length;
    return Math.round((complete / present.length) * 100);
  })();

  return (
    <div data-testid="client-wedding-page" className="pb-24">
      <header className="bg-[#203240] text-white px-5 md:px-10 pt-10 pb-8">
        <p className="overline text-[#E6CFA1]">Espace privé · {w.reference}</p>
        <h1 data-testid="wedding-title" className="font-display text-3xl md:text-5xl mt-3 font-light">
          {w.event_title}
        </h1>
        <p data-testid="portal-intro" className="mt-3 text-white/70 text-sm md:text-base">
          {presentation.intro}
        </p>
        <p className="mt-1 text-white/60 text-sm">{frDate(w.event_date)}</p>
        <div className="mt-6 max-w-md">
          <div className="flex items-center justify-between text-xs text-white/60">
            <span>Avancement des choix</span>
            <span data-testid="wedding-progress">{progress} %</span>
          </div>
          <div className="mt-2 h-1 bg-white/15">
            <div className="h-1 bg-[#B8956A] transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
        {w.locked && (
          <p data-testid="wedding-locked-banner" className="mt-6 text-xs uppercase tracking-[0.18em] text-[#E6CFA1] border border-[#B8956A]/50 inline-block px-3 py-2">
            {w.cancelled ? "Dossier annulé — contactez le restaurant" : "Informations confirmées — contactez-nous pour toute modification"}
          </p>
        )}
      </header>

      <nav className="sticky top-0 z-20 bg-[#F6F0E7]/95 backdrop-blur border-b border-[#D9D3C5] flex overflow-x-auto">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            data-testid={`wedding-tab-${tb.key}`}
            onClick={() => setTab(tb.key)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-4 text-xs md:text-sm whitespace-nowrap transition-colors ${
              tab === tb.key ? "text-[#B8956A] border-b-2 border-[#B8956A]" : "text-[#203240]/70"
            }`}
          >
            <tb.icon size={14} />
            <span className="sm:hidden">{tb.short}</span>
            <span className="hidden sm:inline">{tb.label}</span>
          </button>
        ))}
      </nav>

      <div className="px-5 md:px-10 pt-5 flex items-center justify-between gap-4 min-h-[28px]">
        <SaveBadge state={saveState} />
        {error && <p data-testid="portal-inline-error" className="text-xs text-red-600 text-right">{error}</p>}
      </div>

      <main className="px-5 md:px-10 py-6 max-w-[1100px]">
        <Tab wedding={w} catalog={data.catalog} presentation={presentation} patch={patch} reload={load} setError={setError} />
      </main>
    </div>
  );
}
